import maxBy from 'lodash/maxBy';
import minBy from 'lodash/minBy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Quote } from '../features/quoting/quoteTypes';
import { getActionRequiredQuotes } from '../features/quoting/quoteUtils';
import useQuoteData from '../features/quoting/useQuoteData';
import { useForceUpdate } from '../hooks/useForceUpdate';
import styled from '../theme';
import { calculateTimeLeft } from '../utilities/dateUtils';
import { Flex } from './Flex';

const Container = styled(Flex)`
  background-color: ${({ theme }) => theme.colors.textOne};
  margin-top: 2px;
  height: 32px;
`;

const Text = styled(Flex)`
  color: ${({ theme }) => theme.colors.backgroundOne};
  padding-left: 64px;
`;

const StyledLink = styled(Link)`
  color: ${({ theme }) => theme.colors.textSeven};
`;

const Divider = styled.div`
  margin: 0px 10px;
  background-color: ${({ theme }) => theme.colors.textSeven};
  width: 1px;
  height: 10px;
`;

interface TextWithTimerProps {
  quote: Quote;
  forceUpdate: () => void;
  isMultiple?: boolean;
  timedNotification?: boolean;
  url: string;
}

const TextWithTimer = ({
  quote: { createdAt, status, amount },
  forceUpdate,
  isMultiple = false,
  timedNotification = true,
  url,
}: TextWithTimerProps) => {
  const { t } = useTranslation();

  const calculateFifteenMinutesTimeLeft = () => {
    if (createdAt) {
      return calculateTimeLeft(createdAt, 10);
    }
  };

  const [timeLeft, setTimeLeft] = React.useState(
    calculateFifteenMinutesTimeLeft()
  );
  React.useEffect(() => {
    if (timedNotification) {
      const timer = setTimeout(() => {
        const timeLeft = calculateFifteenMinutesTimeLeft();
        if (
          timeLeft &&
          timeLeft.minutes === '00' &&
          timeLeft.seconds === '00'
        ) {
          forceUpdate();
        }
        setTimeLeft(timeLeft);
      }, 1000);
      return () => clearTimeout(timer);
    }
  });

  if (isMultiple && timedNotification) {
    return (
      <Text alignItems="center">
        Multiple quotes requiring action. {timeLeft?.minutes}:
        {timeLeft?.seconds}
        <Divider />
        <StyledLink to={`${url}`}>{t('view_quotes')}</StyledLink>
      </Text>
    );
  }

  //Single Notifications

  let textToShow = '';
  switch (status.toLowerCase()) {
    case 'denied':
      textToShow = `${amount.toLocaleString()} ${t('Quote declined.')}`;
      break;
    case 'countered':
      textToShow = `${t('action_required_countered')} ${timeLeft?.minutes}:
        ${timeLeft?.seconds}`;
      break;
    default:
      //we should never get here. if we do, its because
      //status is something we dont show notification for.
      textToShow = 'Action Required';
  }

  return (
    <Text alignItems="center">
      {textToShow}
      <Divider />
      <StyledLink to={`${url}`}>{t('view_quote')}</StyledLink>
    </Text>
  );
};

const NotificationBar = (): React.ReactElement | null => {
  const { data } = useQuoteData();
  const forceUpdate = useForceUpdate();
  const baseUrl = window.location.href.includes('findLoads')
    ? `/freight/findLoads`
    : `/freight/myLanes`;

  if (!data || data.length < 1) {
    return null;
  }

  const expiredTimer = 10;
  //Note: we do not dismiss/hide notifications via state/code
  //the only way to remove them from the UI is
  //for user to Discard the quote notification in
  //the drawer.

  const [deniedQuotes, counteredQuotes] = getActionRequiredQuotes(
    expiredTimer,
    data
  );

  if (deniedQuotes.length > 0) {
    //Denied quotes just shows latest denied quote amount
    //even if there are multiple;
    const quote = maxBy(deniedQuotes, (x) => x.createdAt) || deniedQuotes[0];
    const isMultiple = false;
    const destination = `${baseUrl}?id=${quote?.orderId}&quotes=true`;

    return (
      <Container>
        <TextWithTimer
          forceUpdate={forceUpdate}
          quote={quote}
          url={destination}
          isMultiple={isMultiple}
          timedNotification={false}
        />
      </Container>
    );
  }

  if (counteredQuotes.length > 0) {
    const quote =
      minBy(counteredQuotes, (x) => x.createdAt) || counteredQuotes[0];
    const isMultiple = counteredQuotes.length > 1;
    const destination = isMultiple
      ? `${baseUrl}?quotes=true`
      : `${baseUrl}?id=${quote?.orderId}&quotes=true`;

    return (
      <Container>
        <TextWithTimer
          forceUpdate={forceUpdate}
          quote={quote}
          url={destination}
          isMultiple={isMultiple}
        />
      </Container>
    );
  }

  return null;
};

export default NotificationBar;
